import React from "react";
import theme from "../config/theme";
import { Template } from "../pages/richMenu/template.dialog";
import { FaRegKeyboard } from "react-icons/fa";

interface MobileSimulatorProps {
  className?: string;
  template?: Template;
  imageUrl?: string | null;
  showOnChatOpen?: boolean;
  menuBarText?: string;
}

const MobileSimulator: React.FC<MobileSimulatorProps> = ({
  className = "",
  template,
  imageUrl,
  showOnChatOpen = true,
  menuBarText = "",
}) => {
  // Generate labels for template cells (A, B, C, etc.)
  const generateCellLabels = (layout: string): string[] => {
    const labels: string[] = [];
    const layoutMap: Record<string, number> = {
      "2x3": 6,
      "2x2": 4,
      "1-2equal": 3,
      "1-2unequal": 3,
      "2rows": 2,
      "2cols": 2,
      "1rect": 1,
      "1x3": 3,
      "1x2bigRight": 2,
      "1x2bigLeft": 2,
      "1x2equal": 2,
      "1rectSmall": 1,
    };
    const count = layoutMap[layout] || 1;
    for (let i = 0; i < count; i++) {
      labels.push(String.fromCharCode(65 + i)); // A, B, C, ...
    }
    return labels;
  };

  const LineApp = () => {
    if (!template) {
      return (
        <div
          style={{
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            background: "linear-gradient(135deg, #87CEEB 0%, #98D8E8 100%)",
            height: "100%",
            overflow: "hidden",
            position: "relative",
            borderRadius: "20px",
          }}
        >
          {/* Header */}
          <div
            style={{
              background: "#fff",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              zIndex: 10,
              borderRadius: "20px 20px 0 0",
            }}
          >
            <div
              style={{
                fontWeight: "bold",
                color: "#333",
                fontSize: "14px",
              }}
            >
              JOY CLUB
            </div>
          </div>

          {/* Chat Area */}
          <div
            style={{
              flex: 1,
              padding: "15px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              position: "relative",
              zIndex: 5,
              height: "calc(100% - 120px)",
            }}
          >
            <div
              style={{
                background: "#fff",
                padding: "8px 12px",
                borderRadius: "12px",
                maxWidth: "80%",
                alignSelf: "flex-start",
                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                marginBottom: "8px",
              }}
            >
              <div style={{ color: "#333", fontSize: "12px" }}>Hello!</div>
            </div>
          </div>

          {/* Rich Menu */}
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              background: "#fff",
              zIndex: 10,
              borderRadius: "0 0 20px 20px",
            }}
          >
            <div
              style={{
                background: "#f8f9fa",
                padding: "6px 12px",
                textAlign: "center",
                color: "#666",
                fontSize: "12px",
              }}
            >{` `}</div>
          </div>
        </div>
      );
    }

    const cellLabels = generateCellLabels(template.layout);
    const renderRichMenu = () => {
      const isSmall = template.type === "small";
      const menuHeight = isSmall ? "90px" : "120px";
      const largeAspectRatio = "1.48"; // Large rich menu ratio: 2500x1686 = 1.48:1

      // Container style with background image
      const containerStyle: React.CSSProperties = {
        position: "relative",
        width: "100%",
        height: "100%",
        ...(imageUrl
          ? {
              backgroundImage: `url(${imageUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }
          : {
              background: "#fff",
            }),
      };

      // Helper function to render a cell with label (transparent with border and label)
      const renderCell = (
        label: string,
        style: React.CSSProperties,
        fontSize: string = "14px",
        key?: string | number
      ) => (
        <div
          key={key}
          style={{
            ...style,
            position: "relative",
            background: "transparent",
            border: `1px solid ${theme.color.greenLight}`,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: imageUrl ? "rgba(0,0,0,0.3)" : "transparent",
            }}
          >
            <div
              style={{
                fontSize,
                fontWeight: "bold",
                color: imageUrl ? "#fff" : "#333",
              }}
            >
              {label}
            </div>
          </div>
        </div>
      );

      // Render based on layout
      switch (template.layout) {
        case "1x3":
          return (
            <div style={containerStyle}>
              <div style={{ display: "flex", height: "100%" }}>
                {cellLabels.map((label, idx) =>
                  renderCell(
                    label,
                    {
                      flex: 1,
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: menuHeight,
                    },
                    "16px",
                    idx
                  )
                )}
              </div>
            </div>
          );
        case "2x3":
          return (
            <div style={containerStyle}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                {[0, 1].map((row) => (
                  <div key={row} style={{ display: "flex", flex: 1 }}>
                    {cellLabels
                      .slice(row * 3, (row + 1) * 3)
                      .map((label, idx) =>
                        renderCell(
                          label,
                          {
                            flex: 1,
                            aspectRatio: !isSmall ? "1" : undefined,
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "50px",
                          },
                          "14px",
                          idx
                        )
                      )}
                  </div>
                ))}
              </div>
            </div>
          );
        case "2x2":
          return (
            <div style={containerStyle}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                {[0, 1].map((row) => (
                  <div key={row} style={{ display: "flex", flex: 1 }}>
                    {cellLabels
                      .slice(row * 2, (row + 1) * 2)
                      .map((label, idx) =>
                        renderCell(
                          label,
                          {
                            flex: 1,
                            aspectRatio: !isSmall
                              ? largeAspectRatio
                              : undefined,
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "50px",
                          },
                          "14px",
                          idx
                        )
                      )}
                  </div>
                ))}
              </div>
            </div>
          );
        case "1-2equal":
          return (
            <div style={containerStyle}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                {renderCell(cellLabels[0], {
                  aspectRatio: !isSmall ? "2" : undefined,
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "40px",
                })}
                <div style={{ display: "flex", flex: 1 }}>
                  {cellLabels.slice(1).map((label, idx) =>
                    renderCell(
                      label,
                      {
                        flex: 1,
                        aspectRatio: !isSmall ? largeAspectRatio : undefined,
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "50px",
                      },
                      "14px",
                      idx
                    )
                  )}
                </div>
              </div>
            </div>
          );
        case "1-2unequal":
          return (
            <div style={containerStyle}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                {renderCell(cellLabels[0], {
                  aspectRatio: !isSmall ? "3" : undefined,
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "40px",
                })}
                <div style={{ display: "flex", flex: 1 }}>
                  {renderCell(cellLabels[1], {
                    flex: 2,
                    aspectRatio: !isSmall ? "2" : undefined,
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "50px",
                  })}
                  {renderCell(cellLabels[2], {
                    flex: 1,
                    aspectRatio: !isSmall ? "1" : undefined,
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "50px",
                  })}
                </div>
              </div>
            </div>
          );
        case "2rows":
          return (
            <div style={containerStyle}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                {cellLabels.map((label, idx) =>
                  renderCell(
                    label,
                    {
                      flex: 1,
                      textAlign: "center",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "50px",
                    },
                    "14px",
                    idx
                  )
                )}
              </div>
            </div>
          );
        case "2cols":
          return (
            <div style={containerStyle}>
              <div style={{ display: "flex", height: "100%" }}>
                {cellLabels.map((label, idx) =>
                  renderCell(
                    label,
                    {
                      flex: 1,
                      textAlign: "center",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "100px",
                    },
                    "14px",
                    idx
                  )
                )}
              </div>
            </div>
          );
        case "1rect":
          return (
            <div style={containerStyle}>
              {renderCell(cellLabels[0], {
                width: "100%",
                height: "100%",
                aspectRatio: !isSmall ? largeAspectRatio : undefined,
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "100px",
              })}
            </div>
          );
        case "1x2bigRight":
          return (
            <div style={containerStyle}>
              <div style={{ display: "flex", height: "100%" }}>
                {renderCell(cellLabels[0], {
                  flex: 1,
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: menuHeight,
                })}
                {renderCell(cellLabels[1], {
                  flex: 2,
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: menuHeight,
                })}
              </div>
            </div>
          );
        case "1x2bigLeft":
          return (
            <div style={containerStyle}>
              <div style={{ display: "flex", height: "100%" }}>
                {renderCell(cellLabels[0], {
                  flex: 2,
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: menuHeight,
                })}
                {renderCell(cellLabels[1], {
                  flex: 1,
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: menuHeight,
                })}
              </div>
            </div>
          );
        case "1x2equal":
          return (
            <div style={containerStyle}>
              <div style={{ display: "flex", height: "100%" }}>
                {cellLabels.map((label, idx) =>
                  renderCell(
                    label,
                    {
                      flex: 1,
                      textAlign: "center",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: menuHeight,
                    },
                    "14px",
                    idx
                  )
                )}
              </div>
            </div>
          );
        case "1rectSmall":
          return (
            <div style={containerStyle}>
              {renderCell(cellLabels[0], {
                width: "100%",
                height: "100%",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: menuHeight,
              })}
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <div
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          background: "#93aad6",
          height: "100%",
          overflow: "hidden",
          position: "relative",
          borderRadius: "20px",
        }}
      >
        {/* Chat Area */}
        <div
          style={{
            flex: 1,
            padding: "15px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            position: "relative",
            zIndex: 5,
            marginTop: "40px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            {/* Profile Picture */}
            <div
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              <img
                src="/images/logo_joy-removebg-preview.png"
                alt="JOY CLUB"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </div>

            {/* Message Bubble */}
            <div
              style={{
                background: "#fff",
                padding: "5px 12px",
                borderRadius: "20px",
                maxWidth: "80%",
                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                position: "relative",
              }}
            >
              {/* Bubble tail */}
              <div
                style={{
                  position: "absolute",
                  left: "-5px",
                  bottom: "12px",
                  width: 0,
                  height: 0,
                  borderTop: "1px solid transparent",
                  borderBottom: "6px solid transparent",
                  borderRight: "6px solid #fff",
                }}
              ></div>
              <div style={{ color: "#333", fontSize: "12px" }}>Hello!</div>
            </div>
          </div>
        </div>

        {/* Rich Menu - showRichMenu controls content, showOnChatOpen controls menu bar */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            background: "#fff",
            zIndex: 10,
            borderRadius: "0 0 20px 20px",
          }}
        >
          {/* Rich Menu Content - controlled by showRichMenu */}
          {showOnChatOpen && renderRichMenu()}

          {/* Menu Bar - controlled by showOnChatOpen */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              background: "#f8f9fa",
              padding: "6px 12px",
            }}
          >
            {React.createElement(FaRegKeyboard as any, {
              className: "w-4  h-4",
              style: { color: theme.color.gray500 },
            })}

            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                color: theme.color.gray500,
                fontSize: "12px",
              }}
            >
              {menuBarText || "Menu"} ▾
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={` ${className}`}
      style={{ borderColor: theme.color.light300 }}
    >
      <h3
        className="text-lg text-center font-semibold mb-4"
        style={{ color: theme.color.gray500 }}
      >
        Preview
      </h3>
      <div
        className="relative"
        style={{ height: "600px", display: "flex", justifyContent: "center" }}
      >
        {/* Custom iPhone Mockup */}
        <div
          style={{
            width: "280px",
            height: "500px",
            background: "#1a1a1a",
            borderRadius: "30px",
            padding: "8px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
            position: "relative",
          }}
        >
          {/* Screen */}
          <div
            style={{
              width: "100%",
              height: "100%",
              background: "#000",
              borderRadius: "22px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {/* Notch */}
            <div
              style={{
                position: "absolute",
                top: "0",
                left: "50%",
                transform: "translateX(-50%)",
                width: "120px",
                height: "25px",
                background: "#1a1a1a",
                borderRadius: "0 0 15px 15px",
                zIndex: 20,
              }}
            ></div>

            {/* Content */}
            <LineApp />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileSimulator;
